import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';

export interface Objet {
  id: string;
  nom: string;
  categorie: string;
  sousCategorie?: string;
  quantite: number;
  etat: 'bon' | 'à réparer' | 'incomplet' | 'manquant' | 'hs' | 'à vérifier';
  besoinAchat: boolean;
  remarques?: string;
  sku?: string;
  zoneId?: string;
  boxId?: string;
  inventorie?: boolean;
  dateInventaire?: string;
  inventorieParId?: string;
  createdAt: string;
  updatedAt: string;
  zone?: {
    id: string;
    code: string;
    nom: string;
    theme?: string;
  };
  box?: {
    id: string;
    code: string;
    nom: string;
  };
  photos: Array<{
    id: string;
    url: string;
    s3Key?: string;
  }>;
  user: {
    id: string;
    name?: string;
    email: string;
  };
}

export interface ObjetsFilters {
  search?: string;
  categorie?: string;
  sousCategorie?: string;
  etat?: string;
  couleurPrincipale?: string;
  couleurSecondaire?: string;
  besoinAchat?: boolean;
  sansPhoto?: boolean;
  zoneId?: string;
  boxId?: string;
  page?: number;
  limit?: number;
}

export interface ObjetsResponse {
  objets: Objet[];
  total: number;
  page: number;
  limit: number;
  lastPage: number;
}

export const useObjets = (filters: ObjetsFilters = {}) => {
  return useQuery({
    queryKey: ['objets', filters],
    queryFn: async (): Promise<ObjetsResponse> => {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.categorie) params.append('categorie', filters.categorie);
      if (filters.sousCategorie) params.append('sousCategorie', filters.sousCategorie);
      if (filters.etat) params.append('etat', filters.etat);
      if (filters.couleurPrincipale) params.append('couleurPrincipale', filters.couleurPrincipale);
      if (filters.couleurSecondaire) params.append('couleurSecondaire', filters.couleurSecondaire);
      if (filters.besoinAchat !== undefined) params.append('besoinAchat', filters.besoinAchat.toString());
      if (filters.sansPhoto !== undefined) params.append('sansPhoto', filters.sansPhoto.toString());
      if (filters.zoneId) params.append('zoneId', filters.zoneId);
      if (filters.boxId) params.append('boxId', filters.boxId);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response = await apiClient.get(`/objets?${params.toString()}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useObjet = (id: string) => {
  return useQuery({
    queryKey: ['objet', id],
    queryFn: async (): Promise<Objet> => {
      const response = await apiClient.get(`/objets/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateObjet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Objet>) => {
      const response = await apiClient.post('/objets', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useUpdateObjet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Objet> }) => {
      const response = await apiClient.patch(`/objets/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
      queryClient.invalidateQueries({ queryKey: ['objet', id] });
    },
  });
};

export const useDeleteObjet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/objets/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useMoveObjet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      nouvelleZoneId, 
      nouvelleBoxId 
    }: { 
      id: string; 
      nouvelleZoneId?: string; 
      nouvelleBoxId?: string; 
    }) => {
      const response = await apiClient.post(`/objets/${id}/mouvement`, {
        nouvelleZoneId,
        nouvelleBoxId,
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
      queryClient.invalidateQueries({ queryKey: ['objet', id] });
    },
  });
};

export const useMarquerInventorie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const response = await apiClient.patch(`/objets/${id}/inventorier`, { userId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useMarquerInventorieBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ objetIds, userId }: { objetIds: string[]; userId: string }) => {
      const response = await apiClient.patch(`/objets/bulk/inventorier`, { objetIds, userId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useUpdateBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ objetIds, updates }: { objetIds: string[]; updates: Partial<Objet> }) => {
      const response = await apiClient.patch(`/objets/bulk/update`, { objetIds, updates });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useDeleteBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (objetIds: string[]) => {
      const response = await apiClient.delete(`/objets/bulk/delete`, { data: { objetIds } });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useMoveBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ objetIds, zoneId, boxId }: { objetIds: string[]; zoneId?: string; boxId?: string }) => {
      const response = await apiClient.patch(`/objets/bulk/move`, { objetIds, zoneId, boxId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
    },
  });
};

